import { useEffect } from 'react';

import { filterOutEmptyFilterValues } from '@/helpers/filters-helpers';

import { activeFilterTypeState } from './filter.state';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import type { AtomWithResetType } from '@/types/jotai';

export const useHandleFilters = <Filters extends object>({
  appliedFiltersAtom,
  selectedFiltersAtom,
  updateLocationSearch,
  resetLocationSearch,
}: {
  appliedFiltersAtom: AtomWithResetType<Filters>;
  selectedFiltersAtom: AtomWithResetType<Filters>;
  updateLocationSearch: (filters: Filters) => void;
  resetLocationSearch: () => void;
}) => {
  const [activeFilterType, setActiveFilterType] = useAtom(
    activeFilterTypeState,
  );

  const resetActiveFilterType = useResetAtom(activeFilterTypeState);

  const [selectedFilters, setSelectedFilters] = useAtom(selectedFiltersAtom);
  const resetSelectedFilters = useResetAtom(selectedFiltersAtom);

  const [appliedFilters, setAppliedFilters] = useAtom(appliedFiltersAtom);
  const resetAppliedFilters = useResetAtom(appliedFiltersAtom);

  useEffect(() => {
    return () => {
      resetAppliedFilters();
      resetSelectedFilters();
      resetActiveFilterType();
    };
  }, [resetAppliedFilters, resetSelectedFilters, resetActiveFilterType]);

  useEffect(() => {
    setSelectedFilters({ ...appliedFilters });
  }, [appliedFilters, setSelectedFilters]);

  const clearSearchInput = () => {
    resetAppliedFilters();
  };

  const hasAnyActiveFilters =
    Object.values(selectedFilters).filter(filterOutEmptyFilterValues).length >
    0;

  const handleApplyFilters = () => {
    if (activeFilterType) {
      setActiveFilterType(null);
    }
    updateLocationSearch(selectedFilters);
    setAppliedFilters(selectedFilters);
  };

  const switchToSearchFilter = () => {
    if (hasAnyActiveFilters) {
      handleApplyFilters();
    }

    setActiveFilterType('search');
  };

  const handleSearchInputChange = (searchPhrase: string) => {
    setAppliedFilters(currentAppliedFilters => {
      const freshFilters = {
        ...currentAppliedFilters,
        search: searchPhrase,
      };
      updateLocationSearch(freshFilters);
      return freshFilters;
    });
  };

  const handleResetAppliedFilters = () => {
    resetLocationSearch();
    resetAppliedFilters();
  };

  const clearAppliedFilters = () => {
    handleResetAppliedFilters();
    setActiveFilterType(null);
  };

  const clearSelectedFilters = () => {
    resetSelectedFilters();
    clearAppliedFilters();
  };

  return {
    clearSearchInput,
    clearSelectedFilters,
    clearAppliedFilters,
    handleSearchInputChange,
    switchToSearchFilter,
    handleApplyFilters,
    hasAnyActiveFilters,
  };
};
